import { z } from 'zod'

/**
 * Item de Trabalho — a unidade central de execucao.
 *
 * Convencao de nome obrigatoria (spec 4 e 26): a entidade tecnica e `WorkItem`,
 * mas em pt-BR o label na interface e SEMPRE "Item de Trabalho" / "Itens de
 * Trabalho". Nunca "WorkItem", nunca "Tarefa" como traducao generica. O label
 * vem das chaves `entity.work_item.singular` / `.plural`.
 */

export const workItemSchema = z.object({
  id: z.string().uuid(),
  /** Gerado pelo servidor (WI-0182). Unico e imutavel. */
  code: z.string().min(1),
  title: z.string().min(1).max(300),
  description: z.string(),

  workItemTypeId: z.string().uuid(),
  workItemStatusId: z.string().uuid(),
  workItemPriorityId: z.string().uuid(),

  portfolioNodeId: z.string().uuid().nullable(),
  companyAreaId: z.string().uuid().nullable(),
  assigneeMemberId: z.string().uuid().nullable(),
  companyId: z.string().uuid().nullable(),

  /** ETAPA do fluxo em que o item esta (lanes com etapa usam este campo). */
  flowStepId: z.string().uuid().nullable(),

  /** Hierarquia recursiva, sem limite de profundidade (spec 13.3 e 26). */
  parentWorkItemId: z.string().uuid().nullable(),

  startAt: z.coerce.date().nullable(),
  dueAt: z.coerce.date().nullable(),
  /**
   * Quantidade de dias (2026-08-31). Com duracao e sem data, o cronograma
   * PREVE as datas pela cadeia — ver schedule.ts. Data posta a mao vence.
   */
  durationDays: z.number().int().min(0).max(3650).nullable(),
  /** Antecessor escolhido a mao; null = o irmao anterior na ordem. */
  predecessorId: z.string().uuid().nullable(),
  /** Preenchido automaticamente na transicao para um status terminal. */
  completedAt: z.coerce.date().nullable(),

  /** Quando true, o item so conclui se todos os filhos estiverem concluidos. */
  requiresChildrenCompletion: z.boolean(),

  /** HTML do "Resultado esperado" (RichTextEditor). */
  expectedResult: z.string(),
  /** Recorrencia configurada no painel de Datas. */
  recurrence: z.enum(['never', 'daily', 'weekly', 'monthly', 'yearly']),
  /** Lembrete enviado aos membros antes da entrega. */
  reminder: z.enum(['none', '1d', '2h', '1h']),

  sortOrder: z.number().int(),
  /** Valores dos campos complementares: { fieldKey: valor } (SPEC-20260828-1914). */
  customValues: z.record(z.unknown()),
  /** Privado x publico (2026-08-30). Padrao publico. */
  isPrivate: z.boolean(),
  /**
   * Secoes desligadas no menu "..." do item (2026-08-30): desc, datas,
   * expected, children, comments, files. Vazio = tudo no padrao.
   */
  hiddenSections: z.array(z.string()),
  archivedAt: z.coerce.date().nullable(),
})
export type WorkItem = z.infer<typeof workItemSchema>

export const createWorkItemSchema = workItemSchema
  .omit({ id: true, code: true, completedAt: true, archivedAt: true })
  .partial({
    portfolioNodeId: true,
    companyAreaId: true,
    assigneeMemberId: true,
    companyId: true,
    flowStepId: true,
    parentWorkItemId: true,
    startAt: true,
    dueAt: true,
    durationDays: true,
    predecessorId: true,
    requiresChildrenCompletion: true,
    expectedResult: true,
    recurrence: true,
    reminder: true,
    sortOrder: true,
    customValues: true,
    isPrivate: true,
    hiddenSections: true,
  })
export type CreateWorkItemInput = z.input<typeof createWorkItemSchema>

export const updateWorkItemSchema = createWorkItemSchema.partial()
export type UpdateWorkItemInput = z.input<typeof updateWorkItemSchema>

/**
 * Rotulo em pt-BR de cada campo do item, usado no historico ("Status foi
 * alterado de X para Y"). A chave e o nome FISICO da coluna, que e o que a
 * timeline grava.
 */
export const WORK_ITEM_FIELD_LABELS: Readonly<Record<string, string>> = {
  work_item_title: 'Título',
  work_item_description: 'Descrição',
  work_item_work_item_type_id: 'Tipo',
  work_item_work_item_status_id: 'Status',
  work_item_work_item_priority_id: 'Prioridade',
  work_item_portfolio_node_id: 'Item do portfólio',
  work_item_company_area_id: 'Área',
  work_item_assignee_member_id: 'Responsável',
  work_item_client_id: 'Empresa',
  work_item_parent_work_item_id: 'Item pai',
  work_item_start_at: 'Início',
  work_item_due_at: 'Entrega',
  work_item_completed_at: 'Concluído em',
  work_item_requires_children_completion: 'Exige filhos concluídos',
  work_item_expected_result: 'Resultado esperado',
  work_item_hidden_sections: 'Seções visíveis',
  work_item_duration_days: 'Quantidade de dias',
  work_item_predecessor_id: 'Antecessor',
  work_item_recurrence: 'Recorrência',
  work_item_reminder: 'Lembrete',
  work_item_sort_order: 'Ordem',
  work_item_flow_step_id: 'Etapa',
  work_item_is_private: 'Visibilidade',
  work_item_created: 'Criação',
  work_item_archived_at: 'Arquivamento',
  relation: 'Relação',
  automation: 'Automação',
}

/** Nome amigavel do campo; sem mapa, devolve o proprio campo legivel. */
export function workItemFieldLabel(field: string): string {
  // campos complementares viajam com o rotulo pronto: "custom:<rotulo>"
  if (field.startsWith('custom:')) return field.slice('custom:'.length)
  return WORK_ITEM_FIELD_LABELS[field] ?? field.replace(/^work_item_/, '').replaceAll('_', ' ')
}

// --- Regras de transicao -------------------------------------------------

export type CompletionBlocker =
  | { kind: 'pending-children'; pendingCount: number }
  | { kind: 'pending-required-criteria'; pending: readonly { id: string; code: string; title: string }[] }

/**
 * Verifica se um item pode ir para um status terminal (spec 13.7 e 13.3).
 *
 * Dois bloqueios, ambos configurados por dado e nao por codigo:
 *  1. `requiresChildrenCompletion` ligado e ainda ha filhos abertos;
 *  2. existe criterio de aceite marcado como obrigatorio ainda nao concluido.
 *
 * Retorna a lista de bloqueios para a UI conseguir mostrar exatamente o que
 * falta ("Conclua os criterios obrigatorios antes de finalizar este Item de
 * Trabalho.", spec 21).
 */
export function completionBlockers(input: {
  requiresChildrenCompletion: boolean
  openChildrenCount: number
  pendingRequiredCriteria: readonly { id: string; code: string; title: string }[]
}): CompletionBlocker[] {
  const blockers: CompletionBlocker[] = []
  if (input.requiresChildrenCompletion && input.openChildrenCount > 0) {
    blockers.push({ kind: 'pending-children', pendingCount: input.openChildrenCount })
  }
  if (input.pendingRequiredCriteria.length > 0) {
    blockers.push({ kind: 'pending-required-criteria', pending: input.pendingRequiredCriteria })
  }
  return blockers
}

export type WorkItemMoveError = { kind: 'self-reference' } | { kind: 'cycle'; via: string }

/**
 * Impede autorreferencia e ciclos ao trocar o pai de um item (spec 13.3).
 * `newParentAncestorIds` = caminho do novo pai ate a raiz.
 */
export function validateWorkItemMove(input: {
  workItemId: string
  newParentId: string | null
  newParentAncestorIds: readonly string[]
}): WorkItemMoveError | null {
  if (input.newParentId === null) return null
  if (input.newParentId === input.workItemId) return { kind: 'self-reference' }
  if (input.newParentAncestorIds.includes(input.workItemId)) {
    return { kind: 'cycle', via: input.newParentId }
  }
  return null
}

/** Progresso agregado de um pai: concluidos / total de descendentes diretos. */
export function childProgress(children: readonly { isTerminal: boolean }[]): {
  done: number
  total: number
  ratio: number
} {
  const total = children.length
  const done = children.filter((c) => c.isTerminal).length
  return { done, total, ratio: total === 0 ? 0 : done / total }
}

/**
 * PROGRESSO DE UM ITEM: tudo o que está debaixo dele, Itens de Trabalho e
 * linhas de checklist (bug relatado em 2026-10-08).
 *
 * Antes só contavam os descendentes pela árvore (`parentWorkItemId`). Mas
 * "selecionar itens existentes" num checklist VINCULA a linha ao item sem
 * trocar o pai dele — o item continua onde estava. Um pai que organiza o
 * trabalho em checklists (o "Ajustes e melhorias Orbind", com linhas soltas e
 * itens vinculados, uns concluídos) aparecia com "0 itens" e a barra em 0/0,
 * enquanto o resumo do mesmo item já dizia "8/21".
 *
 * O que entra, a partir do item:
 * - cada FILHO pela árvore, e tudo debaixo dele;
 * - cada linha de checklist VINCULADA: conta o item a que aponta (feito = status
 *   terminal), e tudo debaixo dele;
 * - cada linha SOLTA de checklist (feita = marcada), do item e de tudo o que
 *   foi alcançado — é trabalho escondido ali dentro tanto quanto um filho.
 *
 * O mesmo item conta UMA vez só: o filho que também está num checklist do pai
 * (o caso de "Converter em item") não pesa em dobro, e vínculo que volta para
 * cima (o pai num checklist do filho) não entra em ciclo.
 */
export type ItemDoProgresso = {
  id: string
  parentWorkItemId: string | null
  /** Status terminal = concluído. */
  concluido: boolean
}

export type LinhaDoProgresso = {
  id: string
  isDone: boolean
  linkedWorkItemId: string | null
}

export type Progresso = { total: number; feitos: number }

/**
 * Se a linha do checklist está feita. A VINCULADA segue o status do item (é o
 * que o resumo e o detalhe mostram); a marca da própria linha só vale para a
 * solta, ou quando o item vinculado não está à mão — aí vale o que o servidor
 * já derivou na linha.
 */
export function linhaDeChecklistFeita(
  linha: Pick<LinhaDoProgresso, 'isDone' | 'linkedWorkItemId'>,
  itemVinculado?: Pick<ItemDoProgresso, 'concluido'> | null,
): boolean {
  if (linha.linkedWorkItemId && itemVinculado) return itemVinculado.concluido
  return linha.isDone
}

/**
 * Monta a conta para um conjunto de itens e as linhas de checklist de cada um.
 * Devolve uma função por raiz, com cache: a lista chama a mesma raiz de novo a
 * cada comparação da ordenação.
 *
 * Item VINCULADO que não está em `itens` (arquivado, privado, fora do recorte)
 * ainda conta, pela própria linha — ela continua no checklist, e o resumo a
 * conta. Dois vínculos para o mesmo item de fora contam uma vez.
 */
export function criarProgressoDosItens(
  itens: readonly ItemDoProgresso[],
  linhasDoItem: (workItemId: string) => readonly LinhaDoProgresso[],
): (raizId: string) => Progresso {
  const porId = new Map(itens.map((i) => [i.id, i]))
  const filhosDe = new Map<string, ItemDoProgresso[]>()
  for (const item of itens) {
    if (!item.parentWorkItemId) continue
    const irmaos = filhosDe.get(item.parentWorkItemId) ?? []
    irmaos.push(item)
    filhosDe.set(item.parentWorkItemId, irmaos)
  }

  const cache = new Map<string, Progresso>()
  return (raizId) => {
    const pronto = cache.get(raizId)
    if (pronto) return pronto

    // A raiz entra em `vistos` desde o começo: ela não conta no próprio
    // progresso, nem quando um vínculo volta até ela.
    const vistos = new Set<string>([raizId])
    const fila: string[] = [raizId]
    let total = 0
    let feitos = 0
    const contar = (feito: boolean) => {
      total += 1
      if (feito) feitos += 1
    }
    const alcancar = (item: ItemDoProgresso) => {
      if (vistos.has(item.id)) return
      vistos.add(item.id)
      contar(item.concluido)
      fila.push(item.id)
    }

    while (fila.length > 0) {
      const atual = fila.shift()!
      for (const filho of filhosDe.get(atual) ?? []) alcancar(filho)
      for (const linha of linhasDoItem(atual)) {
        if (!linha.linkedWorkItemId) {
          contar(linha.isDone)
          continue
        }
        const vinculado = porId.get(linha.linkedWorkItemId)
        if (vinculado) {
          alcancar(vinculado)
        } else if (!vistos.has(linha.linkedWorkItemId)) {
          vistos.add(linha.linkedWorkItemId)
          contar(linhaDeChecklistFeita(linha, null))
        }
      }
    }

    const resultado = { total, feitos }
    cache.set(raizId, resultado)
    return resultado
  }
}
